// v3.16.x: Force-login khi token chết, dùng chung cho `avatar sync` + `avatar init`.
//
// Tách file riêng (thay vì nhét vào user-config-store.ts) để getValidIdToken có
// thể mock được qua module boundary trong test — gọi cùng-module (self-call)
// trong ESM KHÔNG đi qua object export đã mock, nên phải tách để vi.mock hoạt động.
import { runLogin } from "../commands/login.js";
import { isNonInteractive } from "./non-interactive-context.js";
import { LoginRequiredError } from "./non-interactive-login-required-error.js";
import { getValidIdToken, NoValidTokenError } from "./user-config-store.js";

export interface EnsureValidTokenOptions {
  /** Tên lệnh ghi vào JSON `login_required` khi bị gác (vd `sync`, `sync-all`). BẮT BUỘC. */
  command: string;
  /**
   * Cho phép mở device flow DÙ đang non-interactive. Chỉ `init` bật: extension chạy
   * `init --non-interactive` cho người CHƯA đăng nhập và dựa vào chính device flow này
   * (`extension/src/lib/login-aware-command-timeouts.ts` nới watchdog 5 phút cho `init`).
   */
  allowLoginWhenNonInteractive?: boolean;
}

// Gọi getValidIdToken() (đã tự refresh nếu cần, verify claims mọi nhánh — xem
// user-config-store.ts). Nếu ném NoValidTokenError (chưa login / refresh_token
// revoked / refresh không trả id_token mới) → chạy interactive login flow rồi
// retry đúng 1 lần. Lỗi KHÁC (network, InvalidIdToken...) → propagate nguyên
// trạng, KHÔNG nuốt, KHÔNG trigger login (login chỉ dành cho "hết credential hợp lệ").
//
// Gác non-interactive (force update D5): không ai ngồi trước máy ⇒ KHÔNG mở device flow
// (poll tới 1800s = tiến trình nền treo), ném `LoginRequiredError` ⇒ `index.ts` in JSON
// `reason: "login_required"` + exit 3.
export async function ensureValidTokenOrLogin(opts: EnsureValidTokenOptions): Promise<string> {
  try {
    return await getValidIdToken();
  } catch (err) {
    if (!(err instanceof NoValidTokenError)) throw err;

    if (isNonInteractive() && opts.allowLoginWhenNonInteractive !== true) {
      throw new LoginRequiredError({ command: opts.command, detail: err.message });
    }

    const { log } = await import("./terminal-logger.js");
    log.info("Token hết hạn hoặc chưa đăng nhập — đăng nhập lại...");

    await runLogin({});

    // Retry sau login. Nếu vẫn fail (vd login thành công nhưng ghi config lỗi) →
    // để lỗi propagate nguyên trạng, KHÔNG nuốt thành vòng lặp vô hạn.
    return await getValidIdToken();
  }
}
